import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import {
  getFlowerSubscriptionSettings,
  updateFlowerSubscriptionSettings,
  type FlowerSubscriptionSettingsScope,
} from './flower-subscription-settings.api'
import type { UpdateFlowerSubscriptionSettingsInput } from './flower-subscription-settings.types'

/** Without an organization, the root key matches every organization; hooks fall back to the route's. */
interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries the fetcher reads; the scoped key builder below sets them. */
type FlowerSubscriptionSettingsQueryKey = [AdminQueryRoot & FlowerSubscriptionSettingsScope]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const flowerSubscriptionSettingsQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'flower-subscription-settings' }, { organizationId }),
  ],
  detail: ({
    organizationId,
  }: FlowerSubscriptionSettingsScope): FlowerSubscriptionSettingsQueryKey =>
    buildScopedAdminQueryKey(flowerSubscriptionSettingsQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'detail',
    }),
}

export const flowerSubscriptionSettingsQueries = {
  detail: ({ organizationId }: FlowerSubscriptionSettingsScope) =>
    queryOptions({
      queryKey: flowerSubscriptionSettingsQueryKeys.detail({ organizationId }),
      queryFn: getFlowerSubscriptionSettings,
    }),
}

export function useFlowerSubscriptionSettings({
  organizationId,
  ...options
}: OrganizationScope &
  QueryParams<ReturnType<typeof flowerSubscriptionSettingsQueries.detail>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...flowerSubscriptionSettingsQueries.detail({
      organizationId: resolvedOrganizationId,
    }),
    ...options,
  })
}

export function useUpdateFlowerSubscriptionSettingsMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: UpdateFlowerSubscriptionSettingsInput) =>
      updateFlowerSubscriptionSettings({
        organizationId,
        ...values,
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(
        flowerSubscriptionSettingsQueryKeys.detail({ organizationId }),
        response
      )
    },
  })
}
